// The photo editors' "generating" overlay (Studio Editor, AI Templates) — the AI photo editor's look: the photo blurred, two
// cloud blobs orbiting the frame, and a pill with the current step (no percentage). The steps advance on a timer from mount.
// `onHide` adds a ✕ that keeps the job running in the background (the page moves it to its recent strip). Fills its parent.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconX } from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import styles from './GeneratingOverlay.module.scss';

/**
 * @param photoUrl     the photo to blur underneath (optional)
 * @param steps        the step labels, already translated
 * @param stepDelays   ms from mount at which steps 2, 3, … show
 * @param onHide       optional — the ✕ "keep it running in the background"
 */
export default function GeneratingOverlay({ photoUrl, steps = [], stepDelays = [], onHide, className }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    useEffect(() => {
        const timers = stepDelays.map((ms, i) => setTimeout(() => setStep(i + 1), ms));
        return () => timers.forEach(clearTimeout);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const label = steps[Math.min(step, steps.length - 1)] || '';

    return (
        <div className={cn('absolute inset-0 z-30 overflow-hidden', className)}>
            {photoUrl && (
                <img src={photoUrl} alt="" draggable="false" className="absolute inset-0 h-full w-full scale-105 select-none object-cover [filter:blur(22px)_brightness(0.6)_saturate(0.4)]" />
            )}
            <div className={styles.fog}>
                <div className={styles.cloudB} />
                <div className={styles.cloudA} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className={cn('flex max-w-full items-center gap-2 rounded-full bg-black/70 px-4 py-1.5 text-xs font-semibold text-white shadow-lg', styles.label)} role="status" aria-live="polite">
                    <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {/* explicit size/colour: the global span rule would shrink it and turn it grey */}
                    <span key={step} className={cn('truncate !text-xs !text-white', styles.stepText)}>{label}</span>
                </div>
            </div>
            {onHide && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            onClick={onHide}
                            aria-label={t('imageGeneration.hideProcessing')}
                            className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-[10px] bg-red-600 text-white shadow-[0_4px_12px_rgba(0,0,0,0.4)] transition-colors hover:bg-red-700"
                        >
                            <IconX size={17} strokeWidth={2.4} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="z-[2000]">{t('imageGeneration.hideProcessing')}</TooltipContent>
                </Tooltip>
            )}
        </div>
    );
}
